import { forwardRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../../ui";

/**
 * Quick-add (spec §9.4, §9.24): one empty field. M1 parses the title only; the full syntax lands in M2
 * (`core/quickadd.ts`) and the syntax hint appears only after three tasks exist.
 */
export const QuickAdd = forwardRef<
  HTMLInputElement,
  { onAdd(title: string): void; disabled?: boolean; showSyntaxHint?: boolean }
>(function QuickAdd({ onAdd, disabled, showSyntaxHint }, ref) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  function submit(e?: FormEvent) {
    e?.preventDefault();
    const title = value.trim();
    if (!title) return;
    onAdd(title);
    setValue("");
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") setValue("");
  }

  return (
    <form onSubmit={submit} className="flex items-center gap-2" data-testid="quick-add">
      <Input
        ref={ref}
        name="quick-add"
        aria-label={t("quickAdd.placeholder")}
        placeholder={showSyntaxHint ? t("quickAdd.placeholderSyntax") : t("quickAdd.placeholder")}
        autoComplete="off"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKey}
      />
    </form>
  );
});
